import { useState, useCallback, useRef } from 'react';
import type { WbsTreeNode } from './types';
import { dateToX } from './wbsUtils';

const LONG_PRESS_MS = 200;

interface Props {
  node: WbsTreeNode;
  minDate: Date;
  dayWidth: number;
  rowHeight: number;
  onDateChange?: (wbsItemId: string, field: 'startDate' | 'endDate', value: string) => void;
}

const DEPTH_STYLES: Record<number, { bg: string; height: number }> = {
  1: { bg: 'bg-blue-500', height: 8 },
  2: { bg: 'bg-blue-300', height: 20 },
  3: { bg: 'bg-green-300', height: 20 },
};

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function WbsGanttBar({ node, minDate, dayWidth, rowHeight, onDateChange }: Props) {
  if (!node.startDate || !node.endDate) return null;

  const style = DEPTH_STYLES[node.depth] || DEPTH_STYLES[3];
  const startX = dateToX(new Date(node.startDate), minDate, dayWidth);
  const endX = dateToX(new Date(node.endDate), minDate, dayWidth) + dayWidth;
  const barWidth = Math.max(endX - startX, 2);
  const topOffset = (rowHeight - style.height) / 2;

  const [dragState, setDragState] = useState<{ side: 'left' | 'right'; deltaX: number } | null>(null);
  const [tooltip, setTooltip] = useState<string | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // 드래그 중 위치 계산
  let displayLeft = startX;
  let displayWidth = barWidth;
  if (dragState) {
    if (dragState.side === 'left') {
      displayLeft = startX + dragState.deltaX;
      displayWidth = Math.max(barWidth - dragState.deltaX, dayWidth);
    } else {
      displayWidth = Math.max(barWidth + dragState.deltaX, dayWidth);
    }
  }

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, side: 'left' | 'right') => {
      e.preventDefault();
      e.stopPropagation();
      if (!onDateChange) return;

      const mouseStartX = e.clientX;

      longPressTimerRef.current = setTimeout(() => {
        const handleMouseMove = (moveEvent: MouseEvent) => {
          const deltaX = moveEvent.clientX - mouseStartX;
          const deltaDays = Math.round(deltaX / dayWidth);

          if (side === 'left' && node.startDate) {
            const newDate = addDays(new Date(node.startDate), deltaDays);
            if (node.endDate && newDate >= new Date(node.endDate)) return;
            setDragState({ side: 'left', deltaX: deltaDays * dayWidth });
            setTooltip(`시작일: ${node.startDate.slice(0, 10)} → ${formatISO(newDate)}`);
          } else if (side === 'right' && node.endDate) {
            const newDate = addDays(new Date(node.endDate), deltaDays);
            if (node.startDate && new Date(node.startDate) >= newDate) return;
            setDragState({ side: 'right', deltaX: deltaDays * dayWidth });
            setTooltip(`종료일: ${node.endDate.slice(0, 10)} → ${formatISO(newDate)}`);
          }
        };

        const handleMouseUp = (upEvent: MouseEvent) => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          setDragState(null);
          setTooltip(null);

          const deltaX = upEvent.clientX - mouseStartX;
          const deltaDays = Math.round(deltaX / dayWidth);
          if (deltaDays === 0) return;

          if (side === 'left' && node.startDate) {
            const newDate = addDays(new Date(node.startDate), deltaDays);
            if (node.endDate && newDate >= new Date(node.endDate)) return;
            onDateChange(node.wbsItemId, 'startDate', formatISO(newDate));
          } else if (side === 'right' && node.endDate) {
            const newDate = addDays(new Date(node.endDate), deltaDays);
            if (node.startDate && new Date(node.startDate) >= newDate) return;
            onDateChange(node.wbsItemId, 'endDate', formatISO(newDate));
          }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }, LONG_PRESS_MS);

      const handleEarlyUp = () => {
        clearTimeout(longPressTimerRef.current);
        document.removeEventListener('mouseup', handleEarlyUp);
      };
      document.addEventListener('mouseup', handleEarlyUp);
    },
    [node, dayWidth, onDateChange],
  );

  // 1depth는 요약 바라 리사이즈 비활성
  const canResize = onDateChange && node.depth >= 2;

  return (
    <div
      className="absolute"
      style={{
        top: topOffset,
        left: displayLeft,
        width: displayWidth,
        height: style.height,
        zIndex: dragState ? 20 : undefined,
      }}
    >
      {/* 메인 바 */}
      <div className={`w-full h-full ${style.bg} rounded-sm opacity-80`} />

      {/* 좌측 리사이즈 핸들 */}
      {canResize && (
        <div
          className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-black/10 rounded-l-sm z-10"
          onMouseDown={(e) => handleResizeStart(e, 'left')}
        />
      )}

      {/* 우측 리사이즈 핸들 */}
      {canResize && (
        <div
          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-black/10 rounded-r-sm z-10"
          onMouseDown={(e) => handleResizeStart(e, 'right')}
        />
      )}

      {/* 툴팁 */}
      {tooltip && (
        <div className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-sm rounded whitespace-nowrap z-30 pointer-events-none">
          {tooltip}
        </div>
      )}

      {/* 1depth 예상 지연 구간 */}
      {node.depth === 1 && node.computed?.expectedDelay && (
        <div
          className="absolute top-0 h-full rounded-r-sm"
          style={{
            left: barWidth,
            width: Math.max(
              dateToX(new Date(node.computed.expectedDelay), minDate, dayWidth) + dayWidth - endX,
              0,
            ),
            backgroundImage:
              'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(239,68,68,0.25) 3px, rgba(239,68,68,0.25) 6px)',
            backgroundColor: 'rgba(254,202,202,0.5)',
          }}
        />
      )}
    </div>
  );
}
